// Guided self-record flow (ARCHITECTURE.md section 7): one screen at a time.
//   welcome (18+?) -> role -> topic -> prompt (record) -> review -> about (consent) -> send -> thanks
// plus: resume (a draft was found on this device) and minor (under 18).
// This module owns the shared state, step changes (focus + announcement), the draft in
// IndexedDB, and clip bookkeeping. Each screen lives in its own module.
import { limits, contactEmail } from '../lib/config.js';
import { logger } from '../lib/log.js';
import { announce } from '../lib/forms.js';
import { newId } from '../lib/ids.js';
import { $, $$, show, readJson, plural } from './dom.js';
import { loadPromptData } from './data.js';
import { openStore } from './store.js';
import { baseType } from './mime.js';
import { stopPlayback } from './player.js';
import { createTopicScreens } from './screen-topics.js';
import { createPromptScreen } from './screen-prompt.js';
import { createReviewScreen } from './screen-review.js';
import { createAboutScreen } from './screen-about.js';
import { createSendScreen } from './screen-send.js';

const log = logger('record');
const STEP_PART = { role: 'record', topic: 'record', prompt: 'record', review: 'review', about: 'about', send: 'send' };
const PARTS = ['record', 'review', 'about', 'send'];
const PART_LABEL = { record: 'Record', review: 'Review', about: 'About you', send: 'Send' };
const RESUMABLE = ['role', 'topic', 'prompt', 'review'];

function newDraft() {
  return {
    id: 'current', submissionId: newId(), adultConfirmed: false, role: null, topicId: null, promptId: null,
    step: null, burnedNos: [], photoUpload: null, uid: null, createdAt: new Date().toISOString(),
  };
}
const byCreated = (a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || ''));

export async function start() {
  const root = $('[data-rec-app]');
  if (!root) return;
  window.OH_RECORD_READY = true;
  const html = document.documentElement;
  html.classList.remove('oh-js-failed');
  html.classList.add('oh-js');

  let data;
  try { data = loadPromptData(); } catch (e) {
    log.error('prompt_data_missing', { message: e && e.message });
    html.classList.remove('oh-js');
    return;
  }
  const cfg = readJson('oh-record-config') || {};
  cfg.contactEmail = contactEmail() || cfg.contactEmail || '';

  const state = {
    step: 'welcome', draft: newDraft(), clips: [], take: null,
    topics: [], topic: null, promptIndex: 0,
    form: null, photo: null, consentAt: null,
    recording: false, sending: false, lastResult: null,
  };
  const actions = {};
  const screens = {};

  const app = {
    root, data, cfg, state, actions, store: null,
    maxClips: () => limits.maxClipsPerSubmission || 5,
    maxSeconds: () => limits.maxClipSeconds || 900,
    on(name, fn) { actions[name] = fn; },
    screens,
  };

  // ---------- persistence ----------
  app.saveDraft = () => (app.store ? app.store.putDraft(state.draft) : null);
  app.saveClip = async (clip, patch) => {
    Object.assign(clip, patch);
    if (app.store) await app.store.updateClip(clip.id, patch);
  };

  // ---------- clips ----------
  app.isFull = () => state.clips.length >= app.maxClips();
  app.keptFor = (promptId) => state.clips.filter((c) => c.promptId === promptId);
  app.withUrl = (c) => { if (!c.url) c.url = URL.createObjectURL(c.blob); return c; };
  const revoke = (c) => { if (c && c.url) { try { URL.revokeObjectURL(c.url); } catch { /* ignore */ } c.url = null; } };

  /** A new finished take (recorded or uploaded). Replaces any earlier unkept take. */
  app.setTake = async (take) => {
    if (state.take && state.take.id !== take.id) await app.discardTake({ silent: true });
    state.take = app.withUrl(take);
    if (app.store) await app.store.putClip({ ...take, kept: false });
  };
  app.discardTake = async ({ silent = false } = {}) => {
    const t = state.take;
    if (!t) return;
    state.take = null;
    revoke(t);
    if (!silent) log.info('take_discard', { promptId: t.promptId, source: t.source });
    if (app.store) await app.store.deleteClip(t.id);
  };
  /** Keep the current take. For a normal question it replaces an earlier kept answer. */
  app.keepTake = async () => {
    const t = state.take;
    if (!t) return null;
    if (!data.isOpenPrompt(t.promptId)) {
      for (const old of app.keptFor(t.promptId)) await app.removeClip(old, { reason: 'replaced' });
    }
    if (app.isFull()) { log.warn('keep_refused_full', { promptId: t.promptId }); return null; }
    const clip = { ...t, kept: true, clipNo: null, uploaded: false, storagePath: null, sizeBytes: t.blob.size };
    state.take = null;
    state.clips.push(clip);
    if (app.store) await app.store.updateClip(clip.id, { kept: true });
    log.info('take_keep', { promptId: clip.promptId, durationSec: Math.round(clip.durationSec || 0), source: clip.source, kept: state.clips.length });
    app.refreshSendbars();
    return clip;
  };
  app.removeClip = async (clip, { reason = 'removed' } = {}) => {
    const i = state.clips.indexOf(clip);
    if (i < 0) return;
    stopPlayback();
    state.clips.splice(i, 1);
    // Its upload path is used up (create-only Storage), so never give that number out again.
    if (clip.uploaded && clip.clipNo) {
      state.draft.burnedNos = [...new Set([...(state.draft.burnedNos || []), clip.clipNo])];
      await app.saveDraft();
    }
    revoke(clip);
    if (app.store) await app.store.deleteClip(clip.id);
    log.info('clip_remove', { promptId: clip.promptId, reason, kept: state.clips.length });
    app.refreshSendbars();
  };
  /** Fresh submission id: every file is sent again under new paths. */
  app.rotateSubmission = async (reason) => {
    const old = state.draft.submissionId;
    state.draft.submissionId = newId();
    state.draft.burnedNos = [];
    state.draft.photoUpload = null;
    for (const c of state.clips) await app.saveClip(c, { clipNo: null, uploaded: false, storagePath: null, uploadedUid: null });
    await app.saveDraft();
    log.warn('submission_id_rotated', { from: old, to: state.draft.submissionId, reason });
  };
  /** Forget everything on this device (after a successful send, or Start over). */
  app.clearAll = async () => {
    stopPlayback();
    state.clips.forEach(revoke);
    revoke(state.take);
    state.clips = [];
    state.take = null;
    state.form = null;
    state.consentAt = null;
    if (screens.about) screens.about.reset();
    state.photo = null;
    state.draft = newDraft();
    state.topic = null;
    state.topics = [];
    state.promptIndex = 0;
    if (app.store) { await app.store.clearAll(); await app.saveDraft(); }
    app.refreshSendbars();
  };

  // ---------- role / topic / prompt selection ----------
  app.setRole = (roleId) => {
    state.draft.role = roleId;
    state.topics = data.topicsFor(roleId);
    app.saveDraft();
  };
  app.openTopic = (topic, index = 0) => {
    state.topic = topic;
    state.promptIndex = Math.max(0, Math.min(index, topic.prompts.length - 1));
    state.draft.topicId = topic.id;
    state.draft.promptId = topic.prompts[state.promptIndex].id;
    app.saveDraft();
  };
  app.setPromptIndex = (index) => {
    state.promptIndex = Math.max(0, Math.min(index, state.topic.prompts.length - 1));
    state.draft.promptId = state.topic.prompts[state.promptIndex].id;
    app.saveDraft();
  };
  /** Find the topic holding a prompt: the person's own list first, then every topic. */
  app.locatePrompt = (promptId) => {
    for (const list of [state.topics, data.topicsFor('all')]) {
      for (const t of list) {
        const i = t.prompts.findIndex((p) => p.id === promptId);
        if (i >= 0) return { topic: t, index: i };
      }
    }
    return null;
  };
  /** The prompt id to send: a prompt that no longer exists falls back to the open prompt. */
  app.promptIdFor = (clip) => (data.promptById(clip.promptId) ? clip.promptId : data.openPromptId);

  // ---------- steps ----------
  function updateStepper(step) {
    const stepper = $('[data-rec-stepper]');
    if (!stepper) return;
    const part = STEP_PART[step];
    stepper.hidden = !part;
    const idx = PARTS.indexOf(part);
    $$('li', stepper).forEach((li, i) => {
      li.classList.toggle('is-done', i < idx);
      li.classList.toggle('is-current', i === idx);
      if (i === idx) li.setAttribute('aria-current', 'step'); else li.removeAttribute('aria-current');
      const st = $('[data-part-state]', li);
      if (st) st.textContent = i < idx ? ' (done)' : '';
    });
  }

  /** Move focus to a heading without the sticky header hiding it, and say where we are. */
  app.focusHeading = (h, message) => {
    if (!h) return;
    window.scrollTo(0, 0);
    try { h.focus({ preventScroll: true }); } catch { h.focus(); }
    if (message !== false) announce(message || h.textContent.trim());
  };

  app.go = (step, opts = {}) => {
    const { focus = true, fromHistory = false } = opts;
    stopPlayback();
    const from = state.step;
    state.step = step;
    // Each screen is a browser history entry, so the phone's Back button goes back one screen.
    if (!fromHistory) {
      try {
        const hs = history.state && history.state.ohStep;
        if (!hs || opts.replace) history.replaceState({ ohStep: step }, '');
        else if (hs !== step) history.pushState({ ohStep: step }, '');
      } catch { /* history can be unavailable in odd embeds */ }
    }
    $$('[data-step]', root).forEach((s) => { s.hidden = s.getAttribute('data-step') !== step; });
    updateStepper(step);
    if (RESUMABLE.includes(step) && state.draft.step !== step) { state.draft.step = step; app.saveDraft(); }
    const screen = screens[step];
    if (screen && screen.onShow) screen.onShow(opts);
    if (from !== step) log.info('step_change', { from, to: step });
    if (focus && !(screen && screen.handlesFocus)) {
      const section = $(`[data-step="${step}"]`, root);
      const h = section && $('.rec-step__h', section);
      const part = STEP_PART[step];
      const msg = part ? `Part ${PARTS.indexOf(part) + 1} of ${PARTS.length}, ${PART_LABEL[part]}. ${h ? h.textContent.trim() : ''}` : undefined;
      app.focusHeading(h, msg);
    }
  };

  app.refreshSendbars = () => {
    const n = state.clips.length;
    $$('[data-sendbar]', root).forEach((bar) => {
      show(bar, n > 0 && !state.recording);
      const t = $('[data-sendbar-text]', bar);
      if (t) t.textContent = `You have ${plural(n, 'answer', 'answers')} ready to send.`;
    });
  };

  // ---------- one click handler for every [data-action] ----------
  root.addEventListener('click', (e) => {
    const b = e.target.closest && e.target.closest('[data-action]');
    if (!b || !root.contains(b)) return;
    const fn = actions[b.getAttribute('data-action')];
    if (!fn) return;
    // Run synchronously inside the click: iOS Safari only lets audio play and the microphone's
    // AudioContext start during the user's own gesture, not in a later microtask.
    const fail = (err) => log.error('action_failed', { action: b.getAttribute('data-action'), message: err && err.message, stack: err && err.stack });
    try {
      const r = fn(b, e);
      if (r && typeof r.catch === 'function') r.catch(fail);
    } catch (err) { fail(err); }
  });

  /** A screen we can safely show for a Back/Forward target, given what exists now. */
  function safeStep(target) {
    const hasClips = state.clips.length > 0;
    const adult = state.draft.adultConfirmed;
    if (target === 'resume') return hasClips || state.take ? 'resume' : 'welcome';
    if (['role', 'topic', 'prompt', 'review', 'about', 'send', 'thanks'].includes(target) && !adult) return 'welcome';
    if (target === 'thanks' || target === 'send') return hasClips ? 'about' : 'welcome';
    if ((target === 'review' || target === 'about') && !hasClips) return state.draft.role ? 'topic' : 'role';
    if (target === 'topic' && !state.draft.role) return 'role';
    if (target === 'prompt' && !state.topic) return state.draft.role ? 'topic' : 'role';
    return target;
  }
  window.addEventListener('popstate', (e) => {
    const target = e.state && e.state.ohStep;
    if (!target) return;
    if (state.recording || state.sending) {
      history.pushState({ ohStep: state.step }, '');
      announce(state.recording ? 'Please stop recording first.' : 'Please wait until sending is finished.');
      return;
    }
    if (state.step === 'prompt' && app.hasUnkeptTake && app.hasUnkeptTake()) {
      history.pushState({ ohStep: 'prompt' }, '');
      app.requestLeavePrompt({ type: 'back' });
      return;
    }
    log.info('history_back', { from: state.step, to: target });
    app.go(safeStep(target), { fromHistory: true });
  });

  // Leaving mid-recording or mid-send loses work in progress: ask the browser to confirm.
  window.addEventListener('beforeunload', (e) => {
    if (state.recording || state.sending) { e.preventDefault(); e.returnValue = ''; }
  });

  // ---------- welcome / minor / resume ----------
  app.on('adult-yes', () => {
    state.draft.adultConfirmed = true;
    app.saveDraft();
    log.info('adult_confirm', { answer: 'yes' });
    app.go('role');
  });
  app.on('adult-no', () => {
    log.info('adult_confirm', { answer: 'no' });
    app.go('minor');
  });
  app.on('to-welcome', () => app.go('welcome'));

  const resumeConfirm = $('[data-resume-confirm]', root);
  app.on('resume-continue', () => {
    log.info('resume_continue', { kept: state.clips.length, take: !!state.take });
    const d = state.draft;
    if (!d.adultConfirmed) { app.go('welcome'); return; }
    if (!d.role) { app.go('role'); return; }
    app.setRole(d.role);
    const target = state.take ? state.take.promptId : (d.step === 'prompt' ? d.promptId : null);
    const loc = target ? app.locatePrompt(target) : null;
    if (loc) { app.openTopic(loc.topic, loc.index); app.go('prompt'); return; }
    if (d.step === 'review' || (state.clips.length && d.step !== 'topic' && d.step !== 'role')) { app.go('review'); return; }
    app.go(d.step === 'role' ? 'role' : 'topic');
  });
  app.on('resume-startover', () => {
    show(resumeConfirm, true);
    app.focusHeading($('[data-resume-confirm-h]', resumeConfirm), false);
  });
  app.on('startover-cancel', () => {
    show(resumeConfirm, false);
    const b = $('[data-action="resume-startover"]', root);
    if (b) b.focus();
  });
  app.on('startover-confirm', async () => {
    log.info('start_over', { kept: state.clips.length, take: !!state.take });
    show(resumeConfirm, false);
    await app.clearAll();
    app.go('welcome');
    announce('Your saved answers were deleted. You can start again.');
  });

  // ---------- screens ----------
  Object.assign(screens, createTopicScreens(app));
  screens.prompt = createPromptScreen(app);
  screens.review = createReviewScreen(app);
  screens.about = createAboutScreen(app);
  Object.assign(screens, createSendScreen(app));

  // ---------- storage + resume ----------
  const note = $('[data-storage-note]', root);
  app.store = await openStore({
    onFailure: () => { show(note, true); log.warn('storage_unavailable', { reason: 'write_failed' }); },
  });
  if (!app.store.persistent) {
    show(note, true);
    log.warn('storage_unavailable', { reason: app.store.reason });
  }

  try { history.replaceState({ ohStep: 'welcome' }, ''); } catch { /* ignore */ }
  await restoreDraft(app);
  log.info('record_ready', { persistent: app.store.persistent, kept: state.clips.length, take: !!state.take });
}

async function restoreDraft(app) {
  const { state, store, data } = app;
  const saved = await store.getDraft();
  if (saved && saved.submissionId) state.draft = { ...newDraft(), ...saved };
  else await app.saveDraft();

  const recs = (await store.getClips()).sort(byCreated);
  const unkept = recs.filter((r) => !r.kept);
  state.clips = recs.filter((r) => r.kept).slice(0, app.maxClips()).map((c) => app.withUrl({ ...c, mimeType: baseType(c.mimeType) }));

  // A recording that was cut off (page closed, battery, crash) left its chunks behind.
  for (const ct of await store.getChunkTakes()) {
    if (ct.blob.size && ct.promptId && ct.complete) {
      const mimeType = baseType(ct.mimeType || ct.blob.type) || 'audio/webm';
      const take = {
        id: ct.takeId, promptId: ct.promptId, blob: new Blob([ct.blob], { type: mimeType }), durationSec: ct.elapsedSec,
        mimeType, source: 'recorded', createdAt: ct.createdAt || new Date().toISOString(), kept: false, recovered: true,
      };
      if (await store.putClip(take)) unkept.push(take);
      log.warn('take_recovered', { promptId: take.promptId, durationSec: Math.round(take.durationSec || 0), bytes: take.blob.size });
    } else {
      log.warn('take_unrecoverable', { promptId: ct.promptId || null, bytes: ct.blob.size, complete: !!ct.complete });
    }
    await store.deleteChunks(ct.takeId);
  }

  // Keep the most recent unkept take; older ones were already replaced.
  unkept.sort(byCreated);
  const latest = unkept.length ? unkept[unkept.length - 1] : null;
  for (const t of unkept) if (t !== latest) await store.deleteClip(t.id);
  if (latest && data.promptById(latest.promptId)) state.take = app.withUrl({ ...latest, mimeType: baseType(latest.mimeType) });
  else if (latest) await store.deleteClip(latest.id);

  app.refreshSendbars();
  const n = state.clips.length;
  if (!n && !state.take) return;

  let text;
  if (n && state.take) text = `You have ${plural(n, 'answer', 'answers')} saved on this device, and one recording you had not kept yet. Nothing has been sent.`;
  else if (n) text = `You have ${plural(n, 'answer', 'answers')} saved on this device. ${n === 1 ? 'It has' : 'They have'} not been sent yet.`;
  else text = 'You have a recording on this device that you had not kept yet. Nothing has been sent.';
  const summary = $('[data-resume-summary]', app.root);
  if (summary) summary.textContent = text;
  const confirmText = $('[data-resume-confirm-text]', app.root);
  if (confirmText) confirmText.textContent = `This deletes ${n + (state.take ? 1 : 0) === 1 ? 'the recording' : 'the recordings'} saved on this device. Nothing has been sent, so ${n + (state.take ? 1 : 0) === 1 ? 'it' : 'they'} will be gone for good.`;
  log.info('resume_offer', { kept: n, take: !!state.take });
  // On page load, do not pull focus: the page reads from the top as usual.
  app.go('resume', { focus: false, replace: true });
}
